import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '@vitesse/base-ui/i18n';
import type { CameraStreamManager, CameraStreamHandle, CameraStreamState } from './camera-stream-manager';
import '@vitesse/base-ui/elements/vitesse-icon';

/*
 * <vitesse-camera-feed> — the ONE camera-preview element, reused EVERYWHERE a
 * feed is shown (the camera card preview, the full-screen detail, a dashboard
 * widget). Self-Contained Component Pattern: its inputs are the camera's identity
 * (gateway + camera id) + the shared CameraStreamManager — it acquires its own
 * stream, gates its own liveness on its own visibility, and renders its own state.
 *
 * Two modes:
 *  • LIVE (default, LAN): a live WebRTC feed while on-screen + tab-visible (the
 *    look-away lifecycle in CameraStreamManager). Direct, free, snappy.
 *  • POSTER (`poster`, cloud): a periodically-refreshed snapshot still instead of
 *    a live feed — so a grid of N cameras costs N small keyframes, not N WebRTC
 *    streams of Hetzner egress. Hovering a card makes THAT card go live (the peek);
 *    leaving releases it (the keep-warm makes a re-hover / the click-into-detail
 *    instant). The poster is the instant placeholder; the live <video> covers it
 *    when the first frame arrives (no black flash).
 *
 * It owns NO media logic — that all lives in CameraStreamManager (shared,
 * refcounted, look-away-pausing, self-healing). The <video> persists across
 * reconnects so a re-handshake shows the frozen last frame, never a black flash.
 */

const STYLE_ID = 'vitesse-camera-feed-styles';
const FEED_CSS = `
vitesse-camera-feed { display: block; }
vitesse-camera-feed:has(.cam-feed-detail) { height: 100%; } /* detail fills its tab */
/* Camera CARD: the preview must be CONTENT-sized, not stretched to fill the grid
   cell — otherwise a camera card next to a tall sibling (on the "All" filter)
   stretches taller than on the "Cameras" filter. Override the base preview's
   height:100% + min-height:150px for camera cards only. The 73px feed cap below
   then keeps the camera card the same height as the other overview cards. The
   preview is left-aligned at ~55% width so the bottom-right stays free for action
   buttons, like every other card's layout. */
vitesse-camera-card .base-overview-card-preview { height: auto; min-height: 0; width: 55%; }
.cam-feed {
  position: relative;
  width: 100%;
  /* Video letterbox — intentionally dark in BOTH themes (a feed reads on dark,
     same as every video player); NOT a theme token. The overlay text below is
     therefore light-on-dark regardless of light/dark mode. */
  background: var(--media-backdrop);
  border-radius: var(--radius-md, 8px);
  overflow: hidden;
  display: flex; align-items: center; justify-content: center;
}
.cam-feed video { width: 100%; height: 100%; display: block; background: var(--media-backdrop); }
/* Card thumbnail: 16:9 capped to 73px (cropped to fill, no letterbox) — the height
   that makes the camera card match the other overview cards exactly (172px). */
.cam-feed-preview { aspect-ratio: 16 / 9; max-height: 73px; }
.cam-feed-preview video { object-fit: cover; }
/* Detail player: fills the tab content area; full frame (letterbox if needed). */
.cam-feed-detail { width: 100%; height: 100%; min-height: 240px; }
.cam-feed-detail video { object-fit: contain; }
.cam-feed-idle video, .cam-feed-error video { opacity: 0; }
/* POSTER mode: the still + the live <video> are STACKED; the video fades in over
   the poster when it goes live (and back to the poster on leave) — no black flash. */
.cam-feed-poster .cam-poster, .cam-feed-poster video { position: absolute; inset: 0; transition: opacity 180ms ease; }
.cam-feed-poster .cam-poster { width: 100%; height: 100%; object-fit: cover; }
.cam-feed-detail.cam-feed-poster .cam-poster { object-fit: contain; }
.cam-poster.is-hidden, .cam-feed-poster video.is-hidden { opacity: 0; pointer-events: none; }
.cam-feed-overlay {
  position: absolute; inset: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 6px; color: color-mix(in srgb, var(--on-media-color) 72%, transparent); /* light-on-dark — the feed is always dark */
  font-size: 12px; pointer-events: none; text-align: center;
}
.cam-feed-overlay vitesse-icon { opacity: 0.8; }
.cam-feed-ring {
  width: 22px; height: 22px; border-radius: 50%;
  border: 2px solid color-mix(in srgb, var(--on-media-color) 25%, transparent);
  border-top-color: var(--blue);
  animation: cam-feed-spin 0.9s linear infinite;
}
@keyframes cam-feed-spin { to { transform: rotate(360deg); } }`;

// Idempotent + update-safe: create the shared stylesheet once, but refresh its
// contents when FEED_CSS changes (so a dev HMR edit applies without a hard reload).
function ensureStyles(): void {
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  if (el.textContent !== FEED_CSS) el.textContent = FEED_CSS;
}

// Poster mode tuning. A tile-sized poster (decoded from the full keyframe on the
// viewer's own hardware) is a few KB; refreshing every ~10s keeps the grid
// "live-ish" at ~1000× less egress than a continuous stream.
const POSTER_MAX_DIM = 480;
const POSTER_REFRESH_MS = 10_000;

@customElement('vitesse-camera-feed')
export class ConnectedCameraFeed extends LitElement {
  @property() gatewayId = '';
  @property() cameraId = '';
  @property({ attribute: false }) cameraStreams?: CameraStreamManager;
  /** preview = card thumbnail; detail = full player. Sizing/chrome only. */
  @property() variant: 'preview' | 'detail' = 'preview';
  /** Cloud grids set this: show a refreshed snapshot POSTER instead of a live feed,
   *  and go live only on hover (saves N streams of Hetzner egress). LAN leaves it
   *  false → always-live (free, snappier, no regression). */
  @property({ type: Boolean }) poster = false;

  @state() private feedState: CameraStreamState = 'idle';
  @state() private posterUrl = '';
  private handle?: CameraStreamHandle;
  private videoEl: HTMLVideoElement | null = null;

  // poster mode
  private io?: IntersectionObserver;
  private onScreen = false;
  private hovering = false;
  private refreshTimer = 0;
  private posterReqId = 0;

  protected createRenderRoot(): HTMLElement {
    return this; // light DOM so the injected styles + tokens apply
  }

  connectedCallback(): void {
    super.connectedCallback();
    ensureStyles();
    if (this.poster) this.startPoster();
    else this.acquire();
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stopPoster();
    this.release();
    this.clearPosterUrl();
  }

  // The element is REUSED across nav (Lit recycles it) — re-init on a target,
  // manager, OR mode swap so it follows the new camera / mode instead of holding
  // the old one.
  protected willUpdate(changed: PropertyValues): void {
    if (!this.isConnected) return;
    if (
      changed.has('gatewayId') ||
      changed.has('cameraId') ||
      changed.has('cameraStreams') ||
      changed.has('poster')
    ) {
      this.stopPoster();
      this.release();
      this.clearPosterUrl();
      if (this.poster) this.startPoster();
      else this.acquire();
    }
  }

  // ── live media (shared CameraStreamManager) ──────────────────────────────────
  private acquire(): void {
    if (this.handle || !this.cameraStreams || !this.gatewayId || !this.cameraId) return;
    const h = this.cameraStreams.acquire(this.gatewayId, this.cameraId);
    h.addEventListener('change', this.onStreamChange);
    this.handle = h;
    // Observe ONLY after the element has rendered (firstUpdated) so `this` has a
    // layout box — an IntersectionObserver on a zero-size element reports
    // not-intersecting and the feed never starts. On a re-acquire (already
    // rendered), observe immediately.
    if (this.hasUpdated) h.observe(this);
    // Instant first-paint bridge for the DETAIL player (poster mode has its own still;
    // LAN preview cards connect fast and don't need N snapshot reads on the grid): fetch
    // the last keyframe and show it under the <video> while the live handshake runs. On
    // the REMOTE/cloud path the cascade + keyframe wait is the multi-second "black" the
    // user sees; a single snapshot request-reply (~hundreds of ms) paints the frame
    // immediately, then live fades in over it.
    if (!this.poster && this.variant === 'detail' && !this.posterUrl) void this.fetchBridgeStill();
    this.onStreamChange();
  }

  // One-shot connect-bridge still for the always-live path. Reuses the snapshot
  // (keyframe) endpoint the poster uses; detail gets a crisper frame than a tile.
  private async fetchBridgeStill(): Promise<void> {
    if (!this.cameraStreams || !this.gatewayId || !this.cameraId) return;
    const id = ++this.posterReqId;
    try {
      const url = await this.cameraStreams.snapshotUrl(this.gatewayId, this.cameraId, {
        maxDimension: this.variant === 'detail' ? 1280 : POSTER_MAX_DIM,
        type: 'image/webp',
      });
      // Drop it if stale (target swapped), unmounted, or live already arrived first.
      if (id !== this.posterReqId || !this.isConnected || this.feedState === 'live') {
        URL.revokeObjectURL(url);
        return;
      }
      const old = this.posterUrl;
      this.posterUrl = url;
      if (old) URL.revokeObjectURL(old);
    } catch {
      /* no keyframe yet / camera offline → fall back to the connecting overlay */
    }
  }
  private release(): void {
    if (!this.handle) return;
    this.handle.removeEventListener('change', this.onStreamChange);
    this.handle.release();
    this.handle = undefined;
    if (this.videoEl) this.videoEl.srcObject = null;
    // Drop back to idle so poster mode reveals the still again (and the empty
    // <video> hides) instead of showing a black frame; live mode re-acquires
    // immediately after on a target swap, which sets the new state.
    this.feedState = 'idle';
  }

  private readonly onStreamChange = (): void => {
    this.feedState = this.handle?.state ?? 'idle';
    this.applyStream();
    // Once live on the always-live path, drop the connect-bridge still: the <video>
    // now holds real frames AND keeps its frozen last frame across a reconnect, which
    // is fresher than the snapshot. (Poster mode keeps its refreshing still.)
    if (!this.poster && this.feedState === 'live' && this.posterUrl) this.clearPosterUrl();
    // Forward live media stats up to the card / detail header so they render as
    // proper base status badges (Connected · kbps · ms) — NOT overlaid here.
    // Deferred one microtask: onStreamChange runs synchronously inside acquire()
    // (called from connectedCallback / willUpdate), i.e. WHILE the parent is
    // committing the render that mounts this feed. The parent turns `feedstats`
    // into a reactive @state set, and doing that in-cycle trips Lit's "scheduled an
    // update after an update completed" warning. The detail is snapshotted now (the
    // handle may release before the microtask); a tick later it's a normal
    // post-update set, and stats are advisory so the delay is imperceptible.
    const detail = { state: this.feedState, bitrateKbps: this.handle?.bitrateKbps ?? 0, rttMs: this.handle?.rttMs ?? 0 };
    queueMicrotask(() =>
      this.dispatchEvent(new CustomEvent('feedstats', { detail, bubbles: true, composed: true })),
    );
  };

  private applyStream(): void {
    if (!this.videoEl) this.videoEl = this.querySelector('video');
    const v = this.videoEl;
    if (!v) return;
    const s = this.handle?.stream ?? null;
    if (v.srcObject !== s) {
      v.srcObject = s;
      if (s) void v.play().catch(() => undefined); // muted autoplay; ignore aborts on rapid swaps
    }
  }

  // ── poster mode (cloud): refreshed still + hover-to-live ──────────────────────
  private startPoster(): void {
    if (!this.cameraStreams || !this.gatewayId || !this.cameraId) return;
    // Gate the poster refresh + the hover-peek on the card being on-screen.
    this.io = new IntersectionObserver(
      (entries) => this.setOnScreen(entries[entries.length - 1]?.isIntersecting ?? false),
      { threshold: 0.01 },
    );
    if (this.hasUpdated) this.io.observe(this);
    void this.refreshPoster(); // first paint ASAP (the IO callback then maintains it)
  }
  private stopPoster(): void {
    this.io?.disconnect();
    this.io = undefined;
    this.stopRefresh();
    this.endHover();
    this.onScreen = false;
  }

  private setOnScreen(vis: boolean): void {
    if (vis === this.onScreen) return;
    this.onScreen = vis;
    if (vis) {
      if (!this.posterUrl) void this.refreshPoster();
      this.startRefresh();
    } else {
      this.stopRefresh();
      this.endHover(); // scrolled away mid-peek → drop the live stream
    }
  }
  private startRefresh(): void {
    if (this.refreshTimer) return;
    this.refreshTimer = window.setInterval(() => {
      if (!this.hovering && !document.hidden) void this.refreshPoster();
    }, POSTER_REFRESH_MS);
  }
  private stopRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = 0;
    }
  }
  private async refreshPoster(): Promise<void> {
    if (!this.cameraStreams || !this.gatewayId || !this.cameraId) return;
    const id = ++this.posterReqId; // guard against stale/overlapping fetches
    try {
      const url = await this.cameraStreams.snapshotUrl(this.gatewayId, this.cameraId, {
        maxDimension: POSTER_MAX_DIM,
        type: 'image/webp',
      });
      if (id !== this.posterReqId || !this.isConnected) {
        URL.revokeObjectURL(url);
        return;
      }
      const old = this.posterUrl;
      this.posterUrl = url;
      if (old) URL.revokeObjectURL(old);
    } catch {
      /* camera offline / no keyframe → keep the previous poster (or the idle icon) */
    }
  }
  private clearPosterUrl(): void {
    if (this.posterUrl) {
      URL.revokeObjectURL(this.posterUrl);
      this.posterUrl = '';
    }
  }

  // Hover → live (the peek). The CameraStreamManager keep-warm makes a re-hover —
  // and the click into the always-live detail — instant, because the PC stays warm
  // briefly after release.
  private readonly onPointerEnter = (): void => {
    if (this.poster) this.startHover();
  };
  private readonly onPointerLeave = (): void => {
    if (this.poster) this.endHover();
  };
  private startHover(): void {
    if (this.hovering || !this.onScreen) return;
    this.hovering = true;
    this.acquire(); // leased + visible + tab-visible ⇒ goes live
  }
  private endHover(): void {
    if (!this.hovering) return;
    this.hovering = false;
    this.release();
  }

  protected firstUpdated(): void {
    this.videoEl = this.querySelector('video');
    if (this.poster) this.io?.observe(this); // start gating now that there's a layout box
    else {
      this.handle?.observe(this); // acquire() deferred this when it ran before first render
      this.applyStream();
    }
  }
  protected updated(): void {
    // The <video> is static in the template, but re-grab defensively after any
    // re-render so a stream that arrived before firstUpdated still binds.
    if (!this.videoEl) {
      this.videoEl = this.querySelector('video');
      this.applyStream();
    }
  }

  private overlay() {
    // Poster mode: the still IS the resting state — only show chrome while the
    // hover-peek is establishing, or (no poster yet) the camera glyph.
    if (this.poster && this.feedState !== 'live') {
      if (this.hovering && (this.feedState === 'connecting' || this.feedState === 'reconnecting')) {
        return html`<div class="cam-feed-overlay"><div class="cam-feed-ring"></div></div>`;
      }
      if (!this.posterUrl) {
        return html`<div class="cam-feed-overlay"><vitesse-icon name="camera" size="22"></vitesse-icon></div>`;
      }
      return nothing;
    }
    // Always-live with a connect-bridge still up: the frame IS the feedback, so just a
    // quiet ring while live establishes — no "Connecting" text, no black.
    if (this.posterUrl && this.feedState !== 'live') {
      return this.feedState === 'connecting' || this.feedState === 'reconnecting'
        ? html`<div class="cam-feed-overlay"><div class="cam-feed-ring"></div></div>`
        : nothing;
    }
    switch (this.feedState) {
      case 'connecting':
        return html`<div class="cam-feed-overlay">
          <div class="cam-feed-ring"></div>
          <span>${t('camera.overlayConnecting')}</span>
        </div>`;
      case 'reconnecting':
        // Over the frozen last frame — reassure, don't alarm.
        return html`<div class="cam-feed-overlay">
          <div class="cam-feed-ring"></div>
          <span>${t('camera.overlayReconnecting')}</span>
        </div>`;
      case 'error':
        return html`<div class="cam-feed-overlay">
          <vitesse-icon name="triangle-alert" size="22"></vitesse-icon><span>${t('camera.overlayUnavailable')}</span>
        </div>`;
      case 'idle':
        return html`<div class="cam-feed-overlay">
          <vitesse-icon name="camera" size="22"></vitesse-icon>${this.variant === 'detail'
            ? html`<span>${t('camera.overlayPaused')}</span>`
            : nothing}
        </div>`;
      case 'live':
        return nothing; // live = just the video; status lives in the base badges
    }
  }

  render() {
    const live = this.feedState === 'live';
    // A still underlay is present in BOTH modes: the refreshing POSTER (cloud cards)
    // and the one-shot connect BRIDGE (always-live detail/LAN — the last keyframe
    // shown instantly while the live WebRTC handshake completes, so first-paint is
    // the frame, never black). The <video> fades in over it on the first live frame.
    const hasStill = !!this.posterUrl;
    return html`<div
      class="cam-feed cam-feed-${this.variant} cam-feed-${this.feedState}${hasStill ? ' cam-feed-poster' : ''}"
      @pointerenter=${this.onPointerEnter}
      @pointerleave=${this.onPointerLeave}
    >
      <img class="cam-poster${live || !hasStill ? ' is-hidden' : ''}" src=${this.posterUrl || ''} alt="" ?hidden=${!hasStill} />
      <video autoplay muted playsinline class=${hasStill && !live ? 'is-hidden' : ''}></video>
      ${this.overlay()}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-camera-feed': ConnectedCameraFeed;
  }
}
